import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useFetchUserReports from "../../hooks/useFetchUserReports";
import { formatDate } from "../../../utils/dateUtils";
import { capitalizeFirstLetters } from "../../../utils/stringUtils";
import ReportStatusSelector from "../../Admin/AdminDashboard/ReportStatusSelector";
import {
  FetchedReportData,
  ReportStatusType,
  SelectOption,
} from "../../../types";
import reportBadge from "../../../images/reportBadge.png";
import { reportStatusOptions } from "../../SubmitTip/resources";
import ReportSort from "../../Admin/AdminDashboard/ReportSort";
import UnverifiedEmailCard from "./UnverifiedEmailCard";
import { isEmailVerified } from "../../../utils/authHelpers";
import SearchFilter from "../../common/SearchFilter";
import UserReportCardSkeleton from "../../Skeletons/UserReportCardSkeleton";
import { searchReportProperties } from "../../../utils/filterUtils";

const UserDashboard = ({ view }: { view?: "dashboard" | "reportView" }) => {
  const navigate = useNavigate();
  const { reports, fetchingReports, fetchReportsError } = useFetchUserReports();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<ReportStatusType>();
  const [sortOption, setSortOption] = useState<SelectOption | null>(null);
  const [filteredReports, setFilteredReports] = useState<
    FetchedReportData[] | undefined
  >(reports);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const navigateToSubmitReport = () => {
    navigate("/submit");
  };

  // Use debounce to prevent excessive filtering on every keystroke
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Memoize the filtering function to improve performance
  const filterReports = useCallback(() => {
    if (!reports) return;

    let processedReports = [...reports];

    // Apply status filter
    if (statusFilter) {
      processedReports = processedReports.filter(
        (report) => report.status === statusFilter
      );
    }

    // Apply search filter if there's a query
    if (debouncedSearchQuery.trim() !== "") {
      processedReports = processedReports.filter((report) =>
        searchReportProperties(report, debouncedSearchQuery)
      );
    }

    // Apply sorting after filtering
    if (sortOption?.value === "most recent") {
      processedReports.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } else if (sortOption?.value === "today") {
      const today = new Date().toLocaleDateString();
      processedReports = processedReports.filter(
        (report) => new Date(report.createdAt).toLocaleDateString() === today
      );
    } else if (sortOption?.value === "this week") {
      const startOfWeek = new Date();
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
      processedReports = processedReports.filter(
        (report) =>
          new Date(report.createdAt).getTime() >= startOfWeek.getTime()
      );
    }

    setFilteredReports(processedReports);
  }, [reports, sortOption, statusFilter, debouncedSearchQuery]);

  // Apply filtering when dependencies change
  useEffect(() => {
    filterReports();
  }, [filterReports]);

  // Clear state definition for better readability
  const isLoading = fetchingReports;
  const hasError = !!fetchReportsError;
  const hasReports = reports && reports.length > 0;
  const hasFilteredReports = filteredReports && filteredReports.length > 0;

  // Clearly defined display states
  const noReportsState = !isLoading && !hasError && !hasReports;
  const noMatchingReports =
    !isLoading && !hasError && hasReports && !hasFilteredReports;

  return (
    <div className="w-full h-full flex flex-col items-center gap-x-2 sm:gap-x-5 lg:gap-x-[60px] mx-auto max-w-[calc(100%-64px)] xl:max-w-[1200px] xl:gap-x-[90px]">
      <div className="w-full md:w-[75%] pt-8">
        <SearchFilter onSearch={handleSearch} />
      </div>

      <div className="flex flex-col gap-5 mt-8 w-full">
        {!isEmailVerified && <UnverifiedEmailCard />}

        {/* Only show filters when we have reports and no error */}
        {true && (
          <div className="w-full flex justify-between items-start">
            <ReportStatusSelector
              options={[
                { label: "All", value: undefined },
                ...reportStatusOptions,
              ]}
              reportStatus={statusFilter}
              setReportStatus={setStatusFilter}
              disabled={isLoading}
            />

            <ReportSort
              type="sort"
              setSortOption={setSortOption}
              disabled={isLoading || !hasReports || hasError}
              classNames="w-[120px] xs:w-[104px] md:w-[92px]"
            />
          </div>
        )}

        {/* Loading state with skeleton cards */}
        {isLoading && (
          <div className="flex flex-wrap gap-x-4 gap-y-6 w-full max-w-[1296px] mb-6">
            {[...Array(6)].map((_, index) => (
              <UserReportCardSkeleton key={`skeleton-${index}`} />
            ))}
          </div>
        )}

        {/* Error and empty states container */}
        {(hasError || noReportsState || noMatchingReports) && !isLoading && (
          <div className="w-full flex flex-col items-center justify-center py-10 px-3 gap-5">
            {/* Error state - show regardless of other states if there's an error */}
            {hasError && (
              <p className="text-danger text-lg">
                An error occurred while fetching reports. Please try again
                later.
              </p>
            )}

            {/* No reports state - show when there are no reports at all */}
            {noReportsState && !hasError && (
              <>
                <p className="text-gray-4 text-lg">No active reports found.</p>
                <button
                  onClick={navigateToSubmitReport}
                  className="text-white bg-black rounded-[10px] px-[23px] py-3"
                >
                  Submit a report
                </button>
              </>
            )}

            {/* No matching reports state - show when filters exclude all reports */}
            {noMatchingReports && !hasError && (
              <p className="text-gray-4 text-lg">
                No reports match your search criteria. Try adjusting your
                filters.
              </p>
            )}
          </div>
        )}

        {/* Reports list - only show when we have filtered reports and no error */}
        {!isLoading && !hasError && hasFilteredReports && (
          <div className="flex flex-wrap gap-x-4 gap-y-6 w-full max-w-[1296px] mb-6">
            {filteredReports.map((r) => (
              <div
                key={r._id}
                onClick={() => navigate(`/report/${r._id}`)}
                className="flex flex-col gap-y-2 p-2 md:p-4 border border-[#BCC1CA] rounded-[6px] shadow-[0px 0px 1px 0px #171A1F12] shadow-[0px 0px 2px 0px #171A1F1F] w-full xs:w-[calc(50%-8px)] max-w-[281px] cursor-pointer transition-transform duration-200 hover:scale-105"
              >
                <img className="w-full" src={reportBadge} alt="report" />
                <div className="flex flex-col gap-2">
                  <h6 className="font-bold text-base leading-[26px] text-gray-7">
                    {capitalizeFirstLetters(r.status)}
                  </h6>
                  <p className="font-normal text-sm text-gray-400">
                    {formatDate(r.createdAt)}
                  </p>
                  <p className="font-normal text-sm text-gray-400">
                    {capitalizeFirstLetters(r.caseName)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDashboard;
